import React from 'react';
import { Shield, Users, FileCheck, TrendingUp, Video, CheckCircle } from 'lucide-react';
import { Card } from './ui/card';

const Solution = () => {
  return (
    <section id="solution" className="relative py-24 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a2e] via-[#0a1a3e] to-[#0a0a2e]"></div>
      <div className="absolute top-1/2 left-1/4 w-[600px] h-[600px] bg-cyan-500/5 rounded-full blur-3xl"></div>
      <div className="absolute top-1/4 right-1/4 w-[600px] h-[600px] bg-pink-500/5 rounded-full blur-3xl"></div>

      <div className="relative z-10 max-w-7xl mx-auto px-6">
        <div className="text-center mb-16 space-y-4">
          <span className="px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30 text-cyan-400 text-sm font-medium inline-block">
            The Solution
          </span>
          <h2 className="text-4xl lg:text-5xl font-bold text-white">
            <span className="bg-gradient-to-r from-cyan-400 to-pink-500 bg-clip-text text-transparent">
              Stream Shares
            </span>
            {" "}Solves This
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            A comprehensive platform that protects creators and ensures fair compensation for appearances
          </p>
        </div>

        {/* Creator Tools Section */}
        <div className="mb-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-cyan-500/10 border border-cyan-500/30">
                <Shield className="w-5 h-5 text-cyan-400" />
                <span className="text-cyan-400 font-medium">For Creators</span>
              </div>
              
              <h3 className="text-3xl lg:text-4xl font-bold text-white">
                Protect Your Content with
                <span className="bg-gradient-to-r from-cyan-400 to-blue-500 bg-clip-text text-transparent"> Creator Tools</span>
              </h3>
              
              <p className="text-lg text-gray-300 leading-relaxed">
                Take control of your content with powerful tools designed to protect your work 
                and manage appearances effortlessly.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Video, text: "Log people appearing in your videos or streams" },
                  { icon: FileCheck, text: "Create clear digital records and agreements" },
                  { icon: CheckCircle, text: "Manage agreements and payments easily" },
                  { icon: Shield, text: "Protect yourself from future disputes" }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-4 group">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-cyan-400" />
                      </div>
                      <p className="text-gray-300 pt-2 text-lg">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <div className="relative rounded-3xl overflow-hidden border border-cyan-500/20 shadow-2xl shadow-cyan-500/20">
                <img 
                  src="https://images.unsplash.com/photo-1636971828014-0f3493cba88a"
                  alt="Creator Dashboard"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a2e] via-transparent to-transparent"></div>
              </div>
              {/* Decorative glow */}
              <div className="absolute -bottom-6 -right-6 w-40 h-40 bg-cyan-500/30 rounded-full blur-3xl"></div>
            </div>
          </div>
        </div>

        {/* Appearance Claims Section */}
        <div>
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1 relative">
              <div className="relative rounded-3xl overflow-hidden border border-pink-500/20 shadow-2xl shadow-pink-500/20">
                <img 
                  src="https://images.unsplash.com/photo-1664277497095-424e085175e8"
                  alt="Appearance Claims"
                  className="w-full h-auto object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a2e] via-transparent to-transparent"></div>
              </div>
              {/* Decorative glow */}
              <div className="absolute -top-6 -left-6 w-40 h-40 bg-pink-500/30 rounded-full blur-3xl"></div>
            </div>

            <div className="order-1 lg:order-2 space-y-6">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-pink-500/10 border border-pink-500/30">
                <Users className="w-5 h-5 text-pink-400" />
                <span className="text-pink-400 font-medium">For Individuals</span>
              </div>
              
              <h3 className="text-3xl lg:text-4xl font-bold text-white">
                Request Fair Compensation with
                <span className="bg-gradient-to-r from-pink-400 to-purple-500 bg-clip-text text-transparent"> Appearance Claims</span>
              </h3>
              
              <p className="text-lg text-gray-300 leading-relaxed">
                Appeared in someone's content? Get recognized and compensated fairly through 
                our transparent verification process.
              </p>

              <div className="space-y-4">
                {[
                  { icon: Video, text: "Submit a clip or URL where you appear" },
                  { icon: CheckCircle, text: "Verify your appearance automatically" },
                  { icon: TrendingUp, text: "Request fair compensation through structured process" },
                  { icon: FileCheck, text: "Everything handled transparently on the platform" }
                ].map((item, index) => {
                  const Icon = item.icon;
                  return (
                    <div key={index} className="flex items-start gap-4 group">
                      <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-gradient-to-br from-pink-500/20 to-purple-500/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <Icon className="w-6 h-6 text-pink-400" />
                      </div>
                      <p className="text-gray-300 pt-2 text-lg">{item.text}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        {/* Feature Cards */}
        <div className="mt-20 grid md:grid-cols-3 gap-6">
          {[
            {
              icon: Shield,
              title: "Secure Records",
              description: "Blockchain-backed documentation that can't be disputed",
              color: "cyan"
            },
            {
              icon: TrendingUp,
              title: "Fair Pricing",
              description: "Algorithm-based compensation suggestions",
              color: "purple"
            },
            {
              icon: CheckCircle,
              title: "Quick Resolution",
              description: "Resolve claims in days, not months",
              color: "pink"
            }
          ].map((feature, index) => {
            const Icon = feature.icon;
            const colorClasses = {
              cyan: "from-cyan-500/20 to-blue-500/20 border-cyan-500/30 text-cyan-400",
              purple: "from-purple-500/20 to-pink-500/20 border-purple-500/30 text-purple-400",
              pink: "from-pink-500/20 to-red-500/20 border-pink-500/30 text-pink-400"
            };
            
            return (
              <Card 
                key={index}
                className={`group bg-gradient-to-br ${colorClasses[feature.color]} backdrop-blur-sm border p-6 hover:scale-105 transition-all duration-300`}
              >
                <Icon className={`w-12 h-12 ${colorClasses[feature.color].split(' ')[3]} mb-4`} />
                <h4 className="text-xl font-bold text-white mb-2">{feature.title}</h4>
                <p className="text-gray-300">{feature.description}</p>
              </Card>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Solution;
